"use client";

import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="224056" className="flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-5 w-24 bg-muted/20 animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-sm font-default font-medium text-foreground/80 transition-colors hover:text-primary py-2 group">
                {item.label}
                <span className="absolute left-0 bottom-0 w-0 h-[2px] bg-primary transition-all duration-300 group-hover:w-full shadow-[0_0_8px_var(--primary)]" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  return <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-accent/10">
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-background/95 backdrop-blur-2xl border-l-border/30 pt-16 w-[300px]">
        <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
        <div className="flex flex-col gap-6">
          {loading ? <div className="flex flex-col gap-4">
              {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-10 w-full bg-muted/20 animate-pulse rounded-md" />)}
            </div> : <div className="flex flex-col gap-2">
              {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} data-index={i} className="text-lg font-default font-semibold text-foreground/90 hover:text-primary transition-colors py-3 border-b border-border/20" onClick={() => setIsOpen(false)}>
                  {item.label}
                </Link>)}
            </div>}
          <div className="mt-6">
            <CTAButton className="w-full" />
          </div>
        </div>
      </SheetContent>
    </Sheet>;
}
const CTAButton = ({
  className
}: {
  className?: string;
}) => <div className={`relative group ${className || ''}`}>
    <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-accent-foreground rounded-lg blur opacity-30 group-hover:opacity-70 transition duration-500"></div>
    <Button className="relative w-full bg-primary text-primary-foreground hover:bg-primary/90 transition-all font-default font-bold tracking-tight shadow-xl shadow-primary/10 rounded-md">
      Get Started
    </Button>
  </div>;
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        if (href === '/#') {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
          return;
        }
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (hashPart === '') {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: 'smooth'
            });
            return;
          }
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <section id="smb-it-tech-header" className={`bg-background text-foreground sticky top-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-2xl shadow-black/40' : 'bg-background border-b border-transparent'}`}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-between">
        {}
        <Link to="/" className="flex-shrink-0 z-50 relative group">
          <img src="/img/viralcut_logo.png" alt="SMB IT TECH" className="h-8 w-auto object-contain transition-transform duration-300 group-hover:scale-105 !text-[rgba(249,250,251,1)] bg-primary !opacity-[1] rounded-sm" />
        </Link>

        {}
        <div className="hidden md:flex items-center justify-center flex-1 px-12">
          <MenuProvider menu_id="16">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="flex items-center gap-6 z-50">
          <div className="hidden md:block">
            <CTAButton />
          </div>
          <MenuProvider menu_id="16">
            <MobileMenu />
          </MenuProvider>
        </div>
      </div>
    </section>;
}